/**
 * SQL Schema Builder
 * ------------------
 * Parses SQL question text (or explicit question fields) and generates a
 * CREATE TABLE + INSERT INTO setup script for SQLite (Judge0 language 82).
 *
 * The setup script is prepended to the candidate's query on the backend so
 * that all tables exist before the candidate's SELECT runs.
 *
 * Feature flag: window.ENABLE_SQL_AUTO_SCHEMA !== false  (default: enabled)
 */

export const SQL_LANGUAGE_ID = 82; // Judge0 SQLite

// SQL keywords that must never be treated as table names.
const SQL_KEYWORDS = new Set([
  'SELECT','FROM','WHERE','JOIN','ON','AS','GROUP','ORDER','HAVING','LIMIT',
  'UNION','INSERT','UPDATE','DELETE','DROP','ALTER','BEGIN','COMMIT','ROLLBACK',
  'AND','OR','NOT','IN','BETWEEN','LIKE','IS','NULL','TRUE','FALSE',
  'COUNT','SUM','AVG','MAX','MIN','COALESCE','NULLIF',
  'CASE','WHEN','THEN','ELSE','END','IF',
  'CREATE','TABLE','INDEX','VIEW','WITH','DISTINCT','ALL',
  'INNER','LEFT','RIGHT','FULL','CROSS','OUTER','EXISTS',
  'OVER','PARTITION','ROW_NUMBER','RANK','DENSE_RANK','NTILE',
  'LAG','LEAD','FIRST_VALUE','LAST_VALUE','VALUES',
  'PRIMARY','KEY','FOREIGN','REFERENCES','UNIQUE','DEFAULT','NOT',
  'SCHEMA','DATABASE','TASK','PROBLEM','EXPECTED','SAMPLE','DATA',
  'OUTPUT','CONSTRAINT','NOTE','QUERY','RESULT','WRITE','FIND','CALCULATE',
]);

function isSQLKeyword(word) {
  return SQL_KEYWORDS.has((word || '').toUpperCase());
}

// Map verbose SQL types to the SQLite type affinity set.
// SQLite is lenient, but explicit affinity avoids surprising behavior.
function normalizeType(rawType) {
  if (!rawType) return 'TEXT';
  const t = rawType.toUpperCase().replace(/\s*\([^)]*\)/, '').trim();
  if (/^(INT|INTEGER|SMALLINT|BIGINT|TINYINT|INT4|INT8)/.test(t)) return 'INTEGER';
  if (/^(DECIMAL|NUMERIC|FLOAT|DOUBLE|REAL|MONEY|NUMBER)/.test(t))  return 'REAL';
  if (/^(BOOL|BOOLEAN)/.test(t))                                     return 'INTEGER';
  return 'TEXT'; // VARCHAR, CHAR, DATE, TIMESTAMP, TEXT → TEXT affinity
}

/**
 * Scans text lines and extracts:
 *  • CREATE TABLE statements (formal or informal "tableName(col TYPE, ...)")
 *  • INSERT INTO statements (formal or informal "tableName: (row),(row)")
 *
 * Both formats may appear in AI-generated question text.
 */
function extractFromText(text) {
  const clean = text.replace(/\*\*/g, '').replace(/\*/g, '').replace(/`/g, '');
  const lines = clean.split('\n');

  const createStatements = [];
  const insertStatements = [];

  let inCreate  = false;
  let createBuf = '';
  let parenDepth = 0;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line || line.startsWith('--') || line.startsWith('//')) continue;

    // ── Formal multi-line CREATE TABLE ───────────────────────────────────────
    if (/^CREATE\s+TABLE/i.test(line)) {
      inCreate   = true;
      createBuf  = line;
      parenDepth = (line.match(/\(/g) || []).length - (line.match(/\)/g) || []).length;
      if (parenDepth <= 0 && line.includes(')')) {
        inCreate = false;
        createStatements.push(createBuf.endsWith(';') ? createBuf : createBuf + ';');
        createBuf = '';
      }
      continue;
    }

    if (inCreate) {
      createBuf  += '\n' + line;
      parenDepth += (line.match(/\(/g) || []).length - (line.match(/\)/g) || []).length;
      if (parenDepth <= 0) {
        inCreate = false;
        createStatements.push(createBuf.endsWith(';') ? createBuf : createBuf + ';');
        createBuf = '';
      }
      continue;
    }

    // ── Formal INSERT INTO ────────────────────────────────────────────────────
    if (/^INSERT\s+INTO/i.test(line)) {
      insertStatements.push(line.endsWith(';') ? line : line + ';');
      continue;
    }

    // ── Informal schema: tableName(col TYPE, col TYPE, ...) ──────────────────
    // Matches only if: identifier, no SQL keyword, all column entries have a type token.
    // Issue 2: strip SQL type-size parameters (VARCHAR(100), DECIMAL(10,2), etc.) before
    // matching so the outer-paren regex is not confused by nested parens in type specs.
    const lineForSchema = line.replace(
      /\b(VARCHAR|NVARCHAR|CHAR|DECIMAL|NUMERIC|FLOAT|DOUBLE|INTEGER|INT|BIGINT|SMALLINT|TINYINT|BINARY|VARBINARY|DATETIME|TIMESTAMP|TIME|NUMBER|REAL)\s*\(\s*[\d,\s]+\s*\)/gi,
      '$1'
    );
    const infDef = lineForSchema.match(/^([A-Za-z_]\w*)\s*\(([^)]+)\)\s*;?\s*$/);
    if (infDef) {
      const tName   = infDef[1];
      const colsRaw = infDef[2];
      if (!isSQLKeyword(tName)) {
        const cols        = colsRaw.split(',').map(c => c.trim()).filter(Boolean);
        const allHaveType = cols.every(c => c.split(/\s+/).length >= 2);
        if (allHaveType && cols.length >= 1) {
          const colDefs = cols.map(c => {
            const parts = c.split(/\s+/);
            return `  ${parts[0]} ${normalizeType(parts.slice(1).join(' '))}`;
          });
          createStatements.push(
            `CREATE TABLE IF NOT EXISTS ${tName} (\n${colDefs.join(',\n')}\n);`
          );
          continue;
        }
      }
    }

    // ── Informal data: tableName: (v1,v2,...),(v1,v2,...) ────────────────────
    // Also handles multi-line format where data follows "tableName" or "SAMPLE DATA"
    const infData = line.match(/^([A-Za-z_]\w*)\s*:\s*(\(.+)$/);
    if (infData) {
      const tName   = infData[1];
      const valPart = infData[2];
      if (!isSQLKeyword(tName)) {
        const rows = valPart.match(/\([^)]*\)/g);
        if (rows && rows.length > 0) {
          insertStatements.push(`INSERT INTO ${tName} VALUES\n  ${rows.join(',\n  ')};`);
        }
      }
      continue;
    }

    // Handle "tableName" then rows on next lines, OR "SAMPLE DATA" then rows.
    // We look for rows like (v1,v2),(v3,v4)
    if (line.startsWith('(') && line.includes(')')) {
      const rows = line.match(/\([^)]*\)/g);
      if (rows && rows.length > 0) {
        // Find the most recent table created to insert into.
        let targetTable = '';
        if (createStatements.length > 0) {
          const lastCreate = createStatements[createStatements.length - 1];
          const m = lastCreate.match(/CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?([A-Za-z_]\w*)/i);
          if (m) targetTable = m[1];
        }

        if (targetTable) {
          insertStatements.push(`INSERT INTO ${targetTable} VALUES\n  ${rows.join(',\n  ')};`);
        }
      }
    }
  }

  // Flush any unterminated CREATE TABLE buffer
  if (inCreate && createBuf) {
    createStatements.push(createBuf.endsWith(';') ? createBuf : createBuf + ';');
  }

  return { createStatements, insertStatements };
}

/**
 * Builds the SQL setup script to prepend before the candidate's query.
 *
 * @param {object} question      — question object from questions[] state
 * @param {string} questionText  — aiResponse (may differ from question.text)
 * @returns {string}             — SQL setup script, or '' if nothing detected
 */
export function buildSQLSetupScript(question, questionText) {
  if (typeof window !== 'undefined' && window.ENABLE_SQL_AUTO_SCHEMA === false) return '';

  const q = question || {};

  // ── Structured fields (recruiter-defined questions) ──────────────────────
  if (Array.isArray(q.schemaDefinitions) && q.schemaDefinitions.length > 0) {
    const ddl = q.schemaDefinitions.map(s => {
      const t = s.trim();
      return t.endsWith(';') ? t : t + ';';
    });
    const dml = (q.sampleData || []).map(s => {
      const t = s.trim();
      return t.endsWith(';') ? t : t + ';';
    });
    return [...ddl, ...dml].join('\n') + '\n\n';
  }

  // ── Parse from question text ─────────────────────────────────────────────
  const rawText = questionText || q.text || '';
  if (!rawText) return '';

  const { createStatements, insertStatements } = extractFromText(rawText);
  if (createStatements.length === 0) {
    console.warn('[SQL Schema Builder] No CREATE TABLE statements found in question text.');
    return '';
  }

  const script = [
    '-- [ExiHire] Auto-generated table setup',
    ...createStatements,
    ...insertStatements,
    '',
    '',
  ].join('\n');

  console.log('[SQL Schema Builder] Generated setup script:', script);
  return script;
}
